import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { ClientService } from '../../../core/services/client.service';
import { PoolActivityService } from '../../../core/services/pool-activities.service';
import { ProjectService } from '../../../core/services/project.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EmployeeService } from '../../../core/services/employee.service';
import { TemplateService } from '../../../core/services/template.service';
import { DocumentService } from '../../../core/services/document.service';
import { environment } from '../../../../environments/environment';

interface TemplateItem {
  id: string;
  titleKey: string;
  descKey: string;
  show: boolean;
  downloadAction: () => void;
}

@Component({
  selector: 'em-document-template',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ToastModule,
    ProgressSpinnerModule,
    TranslateModule
  ],
  templateUrl: './document-template.component.html',
  styleUrl: './document-template.component.scss',
  providers: [MessageService],
})
export class DocumentTemplateComponent implements OnInit {
  
  downloadStatus: { [key: string]: boolean } = {
    employee: false,
    staffedRole: false,
    salary: false,
    client: false,
    project: false,
    activity: false
  };

  public environment = environment;
  templatesList: TemplateItem[] = [];
  
  constructor(
    private clientService: ClientService,
    private poolActivityService: PoolActivityService,
    private messageService: MessageService,
    private translate: TranslateService,
    private employeeService: EmployeeService,
    private templateService: TemplateService,
    private documentTemplateService: DocumentService,
  ) { }

  ngOnInit() {
    this.initTemplates();
  }

  initTemplates() {
    this.templatesList = [
      {
        id: 'employee',
        titleKey: 'layout.configurations.documentTemplate.employeeImportTemplate',
        descKey: 'layout.configurations.documentTemplate.employeeTemplateDescription',
        show: (environment as any)['showEmployeeImport'] !== false, 
        downloadAction: () => this.downloadEmployeeTemplate()
      },
      {
        id: 'staffedRole',
        titleKey: 'layout.configurations.documentTemplate.stuffedRoleImportTemplate',
        descKey: 'layout.configurations.documentTemplate.stuffedRoleTemplateDescription',
        show: (environment as any)['showStaffedRoleImport'] !== false, 
        downloadAction: () => this.downloadStuffedRoleTemplate()
      },
      {
        id: 'salary',
        titleKey: 'layout.configurations.documentTemplate.salaryImportTemplate',
        descKey: 'layout.configurations.documentTemplate.salaryTemplateDescription',
        show: (environment as any)['showSalaryImport'] !== false, 
        downloadAction: () => this.downloadSalaryTemplate()
      },
      {
        id: 'client',
        titleKey: 'layout.configurations.documentTemplate.clientImportTemplate',
        descKey: 'layout.configurations.documentTemplate.clientTemplateDescription',
        show: (environment as any)['showClientImport'] !== false,
        downloadAction: () => this.downloadTemplate()
      },
      {
        id: 'project',
        titleKey: 'layout.configurations.documentTemplate.projectImportTemplate',
        descKey: 'layout.configurations.documentTemplate.projectTemplateDescription',
        show: (environment as any)['showProjectImport'] !== false,
        downloadAction: () => this.downloadProjectTemplate()
      },
      {
        id: 'activity',
        titleKey: 'layout.configurations.documentTemplate.activityImportTemplate',
        descKey: 'layout.configurations.documentTemplate.activityTemplateDescription',
        show: (environment as any)['showActivityImport'] !== false,
        downloadAction: () => this.activityTemplate()
      }
    ];
  }

  downloadTemplate() {
    this.downloadStatus['client'] = true;
    this.templateService.downloadClientTemplate().subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'client_import_template.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);

        this.downloadStatus['client'] = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('layout.configurations.documentTemplate.downloaded'),
          detail: this.translate.instant('layout.configurations.documentTemplate.clientTemplateDownloaded'),
        });
      },
      error: (err) => {
        this.downloadStatus['client'] = false;
        this.handleError(err);
      }
    });
  }

  activityTemplate() {
    this.downloadStatus['activity'] = true;
    this.templateService.downloadActivityTemplate().subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'activity_import_template.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);

        this.downloadStatus['activity'] = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('layout.configurations.documentTemplate.downloaded'),
          detail: this.translate.instant('layout.configurations.documentTemplate.activityTemplateDownloaded'),
        });
      },
      error: (err) => {
        this.downloadStatus['activity'] = false;
        this.handleError(err);
      }
    });
  }

  downloadProjectTemplate() {
    this.downloadStatus['project'] = true;
    this.templateService.downloadPorjectTemplate().subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'project_import_template.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);

        this.downloadStatus['project'] = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('layout.configurations.documentTemplate.downloaded'),
          detail: this.translate.instant('layout.configurations.documentTemplate.projectTemplateDownloaded'),
        });
      },
      error: (err) => {
        this.downloadStatus['project'] = false;
        this.handleError(err);
      }
    });
  }

  downloadEmployeeTemplate() {
    this.downloadStatus['employee'] = true;
    this.templateService.downloadEmployeeTemplate().subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'employee_import_template.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);

        this.downloadStatus['employee'] = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('layout.configurations.documentTemplate.downloaded'),
          detail: this.translate.instant('layout.configurations.documentTemplate.employeeTemplateDownloaded'),
        });
      },
      error: (err) => {
        this.downloadStatus['employee'] = false;
        this.handleError(err);
      }
    });
  }

  downloadStuffedRoleTemplate() {
    this.downloadStatus['staffedRole'] = true;
    this.templateService.downloadStuffedRoleTemplate().subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'stuffed_role_import_template.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);

        this.downloadStatus['staffedRole'] = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('layout.configurations.documentTemplate.downloaded'),
          detail: this.translate.instant('layout.configurations.documentTemplate.stuffedRoleTemplateDownloaded'),
        });
      },
      error: (err) => {
        this.downloadStatus['staffedRole'] = false;
        this.handleError(err);
      }
    });
  }

  downloadSalaryTemplate() {
    this.downloadStatus['salary'] = true;
    this.documentTemplateService.downloadSalaryTemplate().subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'employee_salary_template.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);
        this.downloadStatus['salary'] = false;
      },
      error: (err: any) => {
        this.downloadStatus['salary'] = false;
        this.handleError(err);
      },
    });
  }

  private handleError(err: any) {
    let errorMessage = this.translate.instant('layout.configurations.documentTemplate.downloadError.default');
    if (err.status === 500) {
      errorMessage = this.translate.instant('layout.configurations.documentTemplate.downloadError.server');
    } else if (err.status === 404) {
      errorMessage = this.translate.instant('layout.configurations.documentTemplate.downloadError.notFound');
    }

    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('layout.configurations.documentTemplate.downloadFailed'),
      detail: errorMessage,
    });
  }
}